import React, { useEffect, useMemo } from "react";
import { Form, message } from "antd";
import CommonDialog from "../../../../global/dialog";
import CommonForm from "../../../../global/form";
import SOURCES from "../../../../../utils/constants";
import { QUERY_KEYS } from "../../../../../lib/query-constants";
import { useGenericQuery } from "../../../../../queries/use-generic-query";
import { interceptor } from "../../../../../utils/interceptor";

const EnhancedAddStaticResidentialIp = ({
  open,
  setOpen,
  mode = "add",
  editRecord = null,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const isAssign = mode === "assign";
  const isEdit = mode === "edit";
  const [loading, setLoading] = React.useState(false);
  const selectedLeadSource = Form.useWatch("leadSource", form);

  const { data: leadSource } = useGenericQuery({
    endpoint: "leadsource/options",
    queryKey: QUERY_KEYS.leadSourceOptions.lists(),
    enabled: open && isAssign,
  });

  const assignLeadSource = selectedLeadSource || SOURCES.LEAD_KNOT;

  const { data: assignableUsers = [], isLoading: assignableUsersLoading } = useGenericQuery({
    endpoint: "admin/proxies",
    queryKey: QUERY_KEYS.staticResidentialIpsAssignableUsers.lists({ leadSource: assignLeadSource }),
    queryParams: { assignableUsers: "true", leadSource: assignLeadSource },
    enabled: open && isAssign,
  });

  const leadSourceOptions = useMemo(() => {
    if (Array.isArray(leadSource) && leadSource.length > 0) {
      return leadSource.map((item) => ({
        label: item.title,
        value: item.key,
      }));
    }
    return [];
  }, [leadSource]);

  const userEmailOptions = useMemo(
    () =>
      (assignableUsers || []).map((user) => ({
        label: user.name ? `${user.name} (${user.email})` : user.email,
        value: user.email,
      })),
    [assignableUsers],
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    if (isAssign) {
      form.setFieldsValue({ leadSource: SOURCES.LEAD_KNOT });
    }
    if (isEdit && editRecord) {
      form.setFieldsValue({
        host: editRecord.host,
        port: editRecord.port,
        login: editRecord.login,
        password: "",
        type: editRecord.type || "http",
        provider: editRecord.provider || "webshare",
      });
    }
  }, [open, form, isAssign, isEdit, editRecord]);

  useEffect(() => {
    if (open && isAssign) {
      form.setFieldValue("userEmail", undefined);
    }
  }, [assignLeadSource, open, isAssign, form]);

  const handleError = (err) => {
    message.error(err?.response?.data?.data?.error || err?.message || "Something went wrong");
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const axiosInstance = interceptor();

      if (isAssign) {
        await axiosInstance.post("admin/proxies/assign", {
          userEmail: values.userEmail,
          leadSource: values.leadSource || SOURCES.LEAD_KNOT,
        });
        message.success("IP assigned");
      } else if (isEdit) {
        const payload = {
          host: values.host,
          port: Number(values.port),
          login: values.login,
          type: values.type || "http",
          provider: values.provider || "webshare",
        };
        if (values.password) payload.password = values.password;
        await axiosInstance.patch(`admin/proxies/${editRecord._id}`, payload);
        message.success("IP updated");
      } else {
        await axiosInstance.post("admin/proxies", {
          host: values.host,
          port: Number(values.port),
          login: values.login,
          password: values.password,
          type: values.type || "http",
          provider: values.provider || "webshare",
        });
        message.success("IP added");
      }

      setOpen(false);
      form.resetFields();
      onSuccess?.();
    } catch (err) {
      if (err?.errorFields) return;
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const leadSourceField = {
    label: "Lead Source",
    name: "leadSource",
    inputType: SOURCES.FORMINPUTTYPES.SELECT,
    placeholder: "Select lead source",
    required: true,
    message: "Lead source is required",
    options: leadSourceOptions,
    span: 24,
  };

  const addFormData = [
    {
      label: "Host",
      name: "host",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "text",
      placeholder: "e.g. 1.2.3.4",
      required: true,
      message: "Host is required",
      span: 12,
    },
    {
      label: "Port",
      name: "port",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "number",
      placeholder: "e.g. 8080",
      required: true,
      message: "Port is required",
      span: 12,
    },
    {
      label: "Login",
      name: "login",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "text",
      placeholder: "Proxy username",
      required: true,
      message: "Login is required",
      span: 12,
    },
    {
      label: "Password",
      name: "password",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "password",
      placeholder: isEdit ? "Leave blank to keep current password" : "Proxy password",
      required: !isEdit,
      message: "Password is required",
      validate: false,
      span: 12,
    },
    {
      label: "Type",
      name: "type",
      inputType: SOURCES.FORMINPUTTYPES.SELECT,
      placeholder: "Select type",
      required: false,
      options: [
        { label: "HTTP", value: "http" },
        { label: "HTTPS", value: "https" },
        { label: "SOCKS5", value: "socks5" },
      ],
      span: 12,
    },
    {
      label: "Provider",
      name: "provider",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "text",
      placeholder: "webshare",
      required: false,
      span: 12,
    },
  ];

  const assignFormData = [
    leadSourceField,
    {
      label: "User",
      name: "userEmail",
      inputType: SOURCES.FORMINPUTTYPES.SELECT,
      placeholder: assignableUsersLoading
        ? "Loading users..."
        : userEmailOptions.length
          ? "Select user"
          : "No eligible users (all have an active IP)",
      required: true,
      message: "User is required",
      options: userEmailOptions,
      disabled: assignableUsersLoading || userEmailOptions.length === 0,
      loading: assignableUsersLoading,
      showSearch: true,
      optionFilterProp: "label",
      span: 24,
    },
  ];

  const title = isAssign ? "Assign IP" : isEdit ? "Edit IP" : "Add IP";
  const description = isAssign
    ? "Assign the next unassigned IP from the pool to a user."
    : isEdit
      ? "Update this static residential IP."
      : "Add a static residential IP to the pool.";

  return (
    <CommonDialog
      open={open}
      setOpen={setOpen}
      title={title}
      description={description}
      okButtonTitle={isAssign ? "Assign" : isEdit ? "Update" : "Add IP"}
      isConfirmButtonLoading={loading}
      onOk={handleSubmit}
      onCancel={() => {
        setOpen(false);
        form.resetFields();
      }}
    >
      <CommonForm form={form} data={isAssign ? assignFormData : addFormData} />
    </CommonDialog>
  );
};

export default EnhancedAddStaticResidentialIp;
